/**
 * TELA 33 — a tradução da política do servidor para a linguagem da tela.
 *
 * Módulo puro (sem React) para ser testado contra a matriz decidida pelo dono (migration 0013):
 * para cada área, a primeira frase cujas capacidades o perfil tem inteiras. Nenhuma frase
 * concede nada — é só como a tela LÊ o que o servidor já decidiu.
 */

export type Regra = { readonly frase: string; readonly exige: readonly string[] };
export type Area = { readonly area: string; readonly regras: readonly Regra[] };

/** Áreas na ordem do design, e as frases do design, da mais ampla para a mais restrita. */
export const AREAS: readonly Area[] = [
  {
    area: 'Visão geral',
    regras: [
      { frase: 'Dashboard completo', exige: ['painel.ver_completo'] },
      { frase: 'Home operacional', exige: ['painel.ver_operacional'] },
      { frase: 'Resumo da auditoria', exige: ['painel.ver_auditoria'] },
    ],
  },
  {
    area: 'Vendas',
    regras: [
      { frase: 'Visualizar todas e exportar', exige: ['vendas.ver_todas', 'vendas.exportar'] },
      { frase: 'Visualizar todas', exige: ['vendas.ver_todas'] },
      { frase: 'Registrar e ver as próprias', exige: ['vendas.registrar', 'vendas.ver_proprias'] },
      { frase: 'Ver as próprias', exige: ['vendas.ver_proprias'] },
    ],
  },
  {
    area: 'Giro de Prateleira',
    regras: [
      { frase: 'Administrar e gerar', exige: ['giro.administrar'] },
      { frase: 'Executar o bloco atribuído', exige: ['giro.executar'] },
      { frase: 'Auditar divergências', exige: ['giro.auditar'] },
    ],
  },
  {
    area: 'Auditoria',
    regras: [
      { frase: 'Visualizar e reabrir casos', exige: ['auditoria.ver_todos', 'auditoria.reabrir'] },
      { frase: 'Analisar e concluir', exige: ['auditoria.analisar'] },
      { frase: 'Visualizar casos', exige: ['auditoria.ver_todos'] },
      { frase: 'Ver os próprios casos', exige: ['auditoria.ver_proprios'] },
    ],
  },
  {
    area: 'Produtos e catálogos',
    regras: [
      { frase: 'Configurar', exige: ['produtos.configurar'] },
      { frase: 'Consultar o permitido', exige: ['produtos.consultar', 'vendas.registrar'] },
      { frase: 'Consultar para o caso', exige: ['produtos.consultar'] },
    ],
  },
  {
    area: 'Premiação',
    regras: [
      { frase: 'Ver da equipe e conferir', exige: ['premiacao.ver_equipe', 'premiacao.conferir'] },
      { frase: 'Ver a própria', exige: ['premiacao.ver_propria'] },
    ],
  },
  {
    area: 'Campanhas',
    regras: [
      { frase: 'Criar, publicar e pausar', exige: ['campanhas.gerenciar'] },
      { frase: 'Ver produtos participantes', exige: ['campanhas.ver'] },
    ],
  },
  {
    area: 'Comunicação',
    regras: [
      { frase: 'Publicar avisos', exige: ['comunicacao.publicar'] },
      { frase: 'Ler o mural', exige: ['comunicacao.ler'] },
    ],
  },
  {
    area: 'Equipe e acessos',
    regras: [
      { frase: 'Gerenciar e aprovar', exige: ['equipe.gerenciar', 'equipe.aprovar'] },
      { frase: 'Aprovar pedidos', exige: ['equipe.aprovar'] },
      { frase: 'Ver a equipe', exige: ['equipe.ver'] },
    ],
  },
  {
    area: 'Configurações',
    regras: [{ frase: 'Administrar', exige: ['configuracoes.administrar'] }],
  },
  // Encomendas (0058, dono 02/10): o Receptor só recebe e confere; o balconista cuida das suas.
  {
    area: 'Encomendas',
    regras: [
      {
        frase: 'Ver todas, vincular e configurar',
        exige: ['encomendas.ver_todas', 'encomendas.configurar'],
      },
      { frase: 'Ver todas', exige: ['encomendas.ver_todas'] },
      {
        frase: 'Consultar, encomendar e ver as próprias',
        exige: ['encomendas.criar', 'encomendas.ver_proprias'],
      },
      { frase: 'Receber e conferir', exige: ['encomendas.receber', 'encomendas.conferir'] },
      { frase: 'Receber', exige: ['encomendas.receber'] },
    ],
  },
  // Registrar falta (0061, dono 02/10): o balconista registra com um clique; o admin vê tudo.
  {
    area: 'Faltas',
    regras: [
      {
        frase: 'Ver faltas, divergências e rankings',
        exige: ['encomendas.ver_faltas'],
      },
      { frase: 'Registrar falta', exige: ['encomendas.registrar_falta'] },
    ],
  },
  // Regra permanente do dono (migration 0030): os dados da LOJA são só de Nicolas e Carlos.
  {
    area: 'Dados da loja',
    regras: [
      {
        frase: 'Ver tudo e tirar o acesso de outro dono',
        exige: ['loja.ver', 'loja.administrar'],
      },
      { frase: 'Vendas gerais, custo, margem e lucro', exige: ['loja.ver'] },
    ],
  },
];

export function fraseDaArea(area: Area, capacidades: ReadonlySet<string>): string | null {
  return area.regras.find((r) => r.exige.every((c) => capacidades.has(c)))?.frase ?? null;
}
